import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, type Currency } from '../../api.ts';
import { Select } from '../ui.tsx';

/**
 * Moeda escolhida na lista do cadastro de moedas, em todas as telas que pedem moeda. Basta estar
 * logado para ver a lista. A moeda já gravada continua aparecendo, mesmo se tiver sido desativada.
 */
export function CurrencySelect({
  id,
  value,
  onChange,
  required = true,
  blank,
  compact = false,
  'aria-label': ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (code: string) => void;
  required?: boolean;
  /** Texto da opção vazia; null quando o campo sempre tem moeda. */
  blank?: string | null;
  /** Só o código, para ficar ao lado do campo de valor. */
  compact?: boolean;
  'aria-label'?: string;
}) {
  const { t, i18n } = useTranslation();
  const options = useQuery({
    queryKey: ['currency-options'],
    queryFn: () => api.get<Pick<Currency, 'code' | 'name'>[]>('/core/currency-options'),
  });
  const list = options.data ?? [];
  return (
    <Select
      id={id}
      required={required}
      aria-label={ariaLabel}
      className={compact ? 'w-28' : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {blank !== null && <option value="">{blank ?? t('common.chooseCurrency')}</option>}
      {value && !list.some((c) => c.code === value) && <option value={value}>{value}</option>}
      {list.map((c) => (
        <option key={c.code} value={c.code}>
          {compact
            ? c.code
            : `${c.code} · ${c.name[i18n.language as 'pt-BR' | 'en'] ?? c.name['pt-BR']}`}
        </option>
      ))}
    </Select>
  );
}
